import { EnumDecoder, ObjectData, StringDecoder } from '@simonbackx/simple-encoding';
import { SimpleError } from '@simonbackx/simple-errors';
import { Language } from './Language.js';
const TranslationEncodeVersion = 370;
export class TranslatedStringPatch {
    constructor(translations) {
        this.translations = translations;
    }
    patch(patch) {
        if (typeof patch.translations === 'string') {
            return patch;
        }
        if (typeof this.translations === 'string' && Object.keys(patch.translations).length === 0) {
            // Ignore
            return this;
        }
        const c = typeof this.translations === 'string' ? new TranslatedStringPatch({}) : new TranslatedStringPatch({ ...this.translations });
        for (const key in patch.translations) {
            if (Object.prototype.hasOwnProperty.call(patch.translations, key)) {
                const value = patch.translations[key];
                c.translations[key] = value;
            }
        }
        return c;
    }
    encode(context) {
        if (typeof this.translations === 'string') {
            return this.translations;
        }
        return { ...this.translations, _isPatch: true };
    }
}
export class TranslatedString {
    constructor(translations = '') {
        this.translations = translations;
    }
    static create(translations = '') {
        return new TranslatedString(translations);
    }
    get isDefault() {
        return typeof this.translations === 'string';
    }
    fallback(str) {
        if (this.length === 0) {
            return str;
        }
        return this.toString();
    }
    get length() {
        return this.toString().length;
    }
    toString() {
        return this.get($getLanguage());
    }
    valueOf() {
        return this.toString();
    }
    trim() {
        return this.toString().trim();
    }
    toLowerCase() {
        return this.toString().toLowerCase();
    }
    toUpperCase() {
        return this.toString().toUpperCase();
    }
    toLocaleLowerCase() {
        return this.toString().toLocaleLowerCase();
    }
    toLocaleUpperCase() {
        return this.toString().toLocaleUpperCase();
    }
    substr(start, length) {
        return this.toString().substr(start, length);
    }
    substring(start, end) {
        return this.toString().substring(start, end);
    }
    normalize(form) {
        return this.toString().normalize(form);
    }
    replace(searchValue, replaceValue) {
        return this.toString().replace(searchValue, replaceValue);
    }
    charAt(index) {
        return this.toString().charAt(index);
    }
    slice(start, end) {
        return this.toString().slice(start, end);
    }
    split(separator, limit) {
        return this.toString().split(separator, limit);
    }
    startsWith(searchString, position) {
        return this.toString().startsWith(searchString, position);
    }
    match(regexp) {
        return this.toString().match(regexp);
    }
    patch(patch) {
        if (typeof patch === 'string') {
            // Set to simple string without translations
            return TranslatedString.create(patch);
        }
        if (patch instanceof TranslatedString) {
            return patch;
        }
        if (!(patch instanceof TranslatedStringPatch) && !(patch instanceof TranslatedString)) {
            return this.patch(TranslatedString.patch(patch));
        }
        if (typeof patch.translations === 'string') {
            // Set to simple string without translations
            return TranslatedString.create(patch.translations);
        }
        if (typeof this.translations === 'string' && Object.keys(patch.translations).length === 0) {
            // Ignore
            return this;
        }
        const c = typeof this.translations === 'string' ? new TranslatedString({}) : new TranslatedString({ ...this.translations });
        for (const key in patch.translations) {
            if (key in patch.translations) {
                const value = patch.translations[key];
                if (value === null) {
                    delete c.translations[key];
                }
                else {
                    c.translations[key] = value;
                }
            }
        }
        return c;
    }
    static patch(patch) {
        if (typeof patch === 'string') {
            return new TranslatedStringPatch({ [$getLanguage()]: patch });
        }
        return new TranslatedStringPatch(patch);
    }
    get languages() {
        if (typeof this.translations === 'string') {
            return [];
        }
        return Object.keys(this.translations);
    }
    getIfExists(language) {
        if (typeof this.translations === 'string') {
            return this.translations;
        }
        if (this.translations[language] !== undefined) {
            return this.translations[language];
        }
        return null;
    }
    get(language) {
        var _a, _b;
        if (typeof this.translations === 'string') {
            return this.translations;
        }
        if (this.translations[language] !== undefined) {
            return this.translations[language];
        }
        const keys = Object.keys(this.translations);
        if (keys.length === 0) {
            return '';
        }
        return (_b = (_a = this.translations[Language.English]) !== null && _a !== void 0 ? _a : this.translations[Language.Dutch]) !== null && _b !== void 0 ? _b : this.translations[keys[0]];
    }
    static field(options) {
        return {
            ...options,
            decoder: TranslatedStringDecoder,
            upgrade: TranslatedString.upgrade,
            downgrade: TranslatedString.downgrade,
            upgradePatch: (str) => {
                if (!str || typeof str !== 'string') {
                    return new TranslatedStringPatch({});
                }
                return new TranslatedStringPatch({ [Language.Dutch]: str }); // Note: before the version we expect the string to be in Dutch
            },
            downgradePatch: (name) => {
                var _a, _b, _c;
                return (_c = (_b = (_a = name.translations[$getLanguage()]) !== null && _a !== void 0 ? _a : name.translations[Language.English]) !== null && _b !== void 0 ? _b : name.translations[Language.Dutch]) !== null && _c !== void 0 ? _c : undefined;
            },
        };
    }
    static upgrade(data) {
        if (typeof data === 'string') {
            return new TranslatedString(data);
        }
        if (data instanceof TranslatedString) {
            return data;
        }
        console.warn('TranslatedString: Upgrading from unknown type', data);
        return new TranslatedString();
    }
    static downgrade(data) {
        return data.toString();
    }
    encode(context) {
        const keys = Object.keys(this.translations);
        if (keys.length === 0) {
            return '';
        }
        if (keys.length === 1) {
            return this.translations[keys[0]];
        }
        return this.translations;
    }
    clone() {
        if (typeof this.translations === 'string') {
            return new TranslatedString(this.translations);
        }
        return new TranslatedString({ ...this.translations });
    }
    _append(str) {
        if (typeof str === 'string') {
            return this.append(new TranslatedString(str));
        }
        const duplicate = this.clone();
        if (typeof duplicate.translations !== 'string') {
            for (const key in duplicate.translations) {
                duplicate.translations[key] += str.get(key);
            }
            return duplicate;
        }
        duplicate.translations = duplicate.translations + str.toString();
        return duplicate;
    }
    append(...str) {
        let duplicate = this;
        for (const s of str) {
            duplicate = duplicate._append(s);
        }
        return duplicate;
    }
}
export class TranslatedStringPatchDecoderStatic {
    decode(data) {
        if (typeof data.value === 'string') {
            // Allowed
            return new TranslatedStringPatch(data.value);
        }
        if (typeof data.value !== 'object') {
            throw new SimpleError({
                code: 'invalid_field',
                message: 'Expected an object',
                field: data.currentField,
            });
        }
        const dictionary = new TranslatedStringPatch({});
        for (const key in data.value) {
            if (Object.prototype.hasOwnProperty.call(data.value, key)) {
                if (key === '_isPatch') {
                    continue;
                }
                const language = new EnumDecoder(Language).decode(new ObjectData(key, data.context, data.addToCurrentField(key)));
                const str = data.field(key).nullable(StringDecoder);
                dictionary.translations[language] = str;
            }
        }
        return dictionary;
    }
    patchType() {
        return TranslatedStringPatchDecoder;
    }
    patchIdentifier() {
        throw new Error('A patched translated string can not be used inside a PatchableArray');
    }
}
export const TranslatedStringPatchDecoder = new TranslatedStringPatchDecoderStatic();
export class TranslatedStringDecoderStatic {
    patchType() {
        return TranslatedStringPatchDecoder;
    }
    patchIdentifier() {
        throw new Error('A translated string can not be used inside a PatchableArray');
    }
    decode(data) {
        if (typeof data.value === 'string') {
            // Allowed
            return new TranslatedString(data.value);
        }
        if (typeof data.value !== 'object') {
            throw new SimpleError({
                code: 'invalid_field',
                message: 'Expected an object (preferred) or string',
                field: data.currentField,
            });
        }
        const dictionary = {};
        for (const key in data.value) {
            if (Object.prototype.hasOwnProperty.call(data.value, key)) {
                const language = new EnumDecoder(Language).decode(new ObjectData(key, data.context, data.addToCurrentField(key)));
                const str = data.field(key).string;
                dictionary[language] = str;
            }
        }
        return new TranslatedString(dictionary);
    }
}
export const TranslatedStringDecoder = new TranslatedStringDecoderStatic();
//# sourceMappingURL=TranslatedString.js.map